<script setup>
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const props = defineProps({ blok: Object })
defineEmits(['action'])

const defaultStats = [
  {
    value: '45+',
    label: 'Years of Experience',
  },
  {
    value: '600+',
    label: 'Projects Completed',
  },
  {
    value: '50+',
    label: 'Countries Served',
  },
]

const textColorClasses = computed(() => {
  switch (props.blok.color) {
    case 'yellow':
      return 'text-yellow-500'
    case 'blue':
      return 'text-blue-500'
    case 'green':
      return 'text-green-500 '
    default:
      return 'text-secondary-red-100 '
  }
})

const bgColorClasses = computed(() => {
  switch (props.blok.color) {
    case 'yellow':
      return 'bg-yellow-500'
    case 'blue':
      return 'bg-blue-500'
    case 'green':
      return 'bg-green-500 '
    default:
      return 'bg-secondary-red-100 '
  }
})

const widthClasses = computed(() => {
  switch (props.blok.width) {
    case 'full':
      return 'w-full'
    default:
      return 'lg:w-[55%] xl:w-[52%]'
  }
})

const paddingClasses = computed(() => {
  switch (props.blok.padding) {
    case 'padding-none':
      return ''
    default :
      return 'lg:pt-desktop-navbar'
  }
})

const heroStats = computed(() => {
  if (props.blok.stats?.length)
    return props.blok.stats

  if (props.blok.title?.toLowerCase().includes('case'))
    return defaultStats

  return []
})

const hasStats = computed(() => heroStats.value.length > 0)

function handleClick(e) {
  e.preventDefault()
}
</script>

<template>
  <div :class="paddingClasses" class="overflow-hidden pt-mobile-navbar" @contextmenu.prevent="handleClick">
    <section class="main-hero relative min-h-[520px] flex items-stretch overflow-hidden bg-primary lg:h-[570px] lg:min-h-0 md:min-h-[560px] xl:h-[600px]">
      <nuxt-img
        v-if="blok.media?.filename"
        width="1920"
        height="760"
        loading="eager"
        sizes="xs:760px sm:1000px md:1280px lg:1600px xl:1920px"
        :alt="blok.title"
        :src="`${blok.media.filename}/m/1920x760/filters:quality(82)`"
        class="main-hero-media absolute inset-0 h-full w-full object-cover object-center"
      />
      <div class="absolute inset-0 bg-primary/20" />
      <div class="absolute inset-0 from-primary/85 via-primary/35 to-primary/0 bg-gradient-to-r" />
      <div class="main-hero-accent pointer-events-none absolute inset-0" />

      <div class="hero-panel pointer-events-none absolute inset-y-0 left-0 w-full bg-[#11183a]/86 lg:w-[54%] xl:w-[51%]" />

      <div class="relative z-10 min-h-[520px] flex items-center py-custom-lg container lg:h-full lg:min-h-0 md:min-h-[560px] lg:py-custom-xl">
        <div class="main-hero-copy max-w-full lg:pr-custom-md" :class="widthClasses">
          <div
            v-if="blok.header"
            class="hero-reveal hero-kicker mb-5 flex items-center text-xs font-bold uppercase md:text-sm"
            :class="textColorClasses"
          >
            <span class="hero-kicker-line mr-3 h-[3px] w-7 rounded-full md:w-8" :class="bgColorClasses" />
            {{ blok.header }}
          </div>
          <h1 class="hero-reveal hero-title max-w-[640px] text-4xl text-white font-bold leading-[1.08] uppercase drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)] md:text-5xl xl:text-6xl">
            {{ blok.title }}
          </h1>
          <div v-if="blok.content" class="hero-reveal columns-content columns-content-dark main-hero-content mt-6 max-w-[620px] text-white/88">
            <RichTextRenderer :document="blok.content" />
          </div>
          <ul v-if="blok.actions?.length" class="hero-reveal mt-8 flex flex-wrap gap-4 gap-y-custom-md p-0">
            <li v-for="action in blok.actions" :key="action.uuid" class="mb-0">
              <StoryblokComponent :blok="action" :theme="blok.color" />
            </li>
          </ul>

          <div v-if="hasStats" class="hero-reveal grid grid-cols-1 mt-9 max-w-[660px] gap-5 sm:grid-cols-3">
            <div v-for="(stat, index) in heroStats" :key="stat._uid || stat.value || index" class="hero-stat flex items-center gap-3">
              <div class="hero-stat-icon h-8 w-8 flex shrink-0 items-center justify-center text-secondary-red-100">
                <div v-if="stat.icon" class="[&>svg]:h-7 [&>svg]:w-7" v-html="stat.icon" />
                <svg v-else-if="index === 1" width="30" height="30" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M4 21q-.825 0-1.412-.587T2 19V8q0-.825.588-1.412T4 6h4V4q0-.825.588-1.412T10 2h4q.825 0 1.413.588T16 4v2h4q.825 0 1.413.588T22 8v11q0 .825-.587 1.413T20 21zm6-15h4V4h-4zm2 8.5q.825 0 1.413-.587T14 12.5h6V8H4v4.5h6q0 .825.588 1.413T12 14.5M4 19h16v-4.5h-6q0 .825-.587 1.413T12 16.5t-1.412-.587T10 14.5H4z" />
                </svg>
                <svg v-else-if="index === 2" width="30" height="30" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 22q-3.475-.875-5.737-3.988T4 11.1V5l8-3l8 3v6.1q0 3.8-2.263 6.913T12 22m0-2.1q1.25-.4 2.325-1.137T16.25 17H12v-2h5.55q.2-.45.325-.925T18.1 13H12v-2h6V6.4l-6-2.25z" />
                </svg>
                <svg v-else width="30" height="30" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 2q1.85 0 3.15 1.288T16.475 6.4q1.5.5 2.513 1.725T20 11q0 1.55-.8 2.788T17.1 15.65q-.55 2.725-2.925 4.538T8.8 22H4q-.825 0-1.412-.587T2 20v-4.8q0-3 1.813-5.375T8.35 6.9q.225-2.075 1.25-3.488T12 2m0 2q-.575 0-1.025.613T10.325 6.2q.425-.05.837-.075T12 6.1q.425 0 .838.025t.837.075q-.2-.975-.65-1.587T12 4M4 20h4.8q2.625 0 4.463-1.838T15.1 13.7V8.9H10.3q-2.625 0-4.462 1.838T4 15.2z" />
                </svg>
              </div>
              <div>
                <div class="text-2xl text-white font-bold leading-none md:text-3xl">
                  {{ stat.value || stat.number }}
                </div>
                <div class="mt-1.5 text-xs text-white/82 font-semibold leading-snug">
                  {{ stat.label || stat.title || stat.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- <div v-if="blok.name" class="pointer-events-none absolute inset-y-0 right-0 z-10 hidden h-full items-center py-12 lg:w-[42%] xl:w-[45%] lg:flex">
        <div id="parent" class="pointer-events-auto relative h-full w-full">
          <Scene
            :label-id="blok.label"
            :scale="1"
            :pos-y="0"
            :ro-y="0"
            :models="[{
              model: `https://cdn.jsdelivr.net/gh/webmaster-dexontechnology/gh-cdn/model/${blok.name}`,
            }]"
            :model-label="true"
            class="z-0 h-full w-full items-center justify-center lg:z-10 lg:flex lg:pl-custom-xl xl:pl-0"
          />
          <div :id="blok.label" class="pointer-events-auto absolute inset-0 hidden h-full w-full lg:flex" />
        </div>
      </div> -->
    </section>
  </div>
</template>

<style scoped>
.main-hero {
  isolation: isolate;
}

.main-hero-media {
  animation: hero-media-drift 1200ms cubic-bezier(0.22, 1, 0.36, 1) both;
  will-change: transform, filter;
}

.main-hero-accent {
  background:
    linear-gradient(115deg, rgba(250, 34, 52, 0.22) 0%, rgba(250, 34, 52, 0.08) 22%, transparent 48%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, transparent 28%);
  mix-blend-mode: screen;
  opacity: 0.72;
}

.hero-panel {
  animation: hero-panel-in 850ms cubic-bezier(0.22, 1, 0.36, 1) both;
  box-shadow: 28px 0 70px rgba(20, 10, 75, 0.32);
  transition:
    width 650ms cubic-bezier(0.22, 1, 0.36, 1),
    clip-path 650ms cubic-bezier(0.22, 1, 0.36, 1),
    background-color 650ms ease,
    box-shadow 650ms ease;
  will-change: width, clip-path;
}

.hero-panel::after {
  position: absolute;
  inset: 0;
  content: "";
  background:
    linear-gradient(120deg, rgba(250, 34, 52, 0.2), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent 46%);
  opacity: 0.78;
}

.hero-reveal {
  animation: hero-copy-in 760ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.hero-reveal:nth-child(1) {
  animation-delay: 140ms;
}

.hero-reveal:nth-child(2) {
  animation-delay: 240ms;
}

.hero-reveal:nth-child(3) {
  animation-delay: 340ms;
}

.hero-reveal:nth-child(4) {
  animation-delay: 440ms;
}

.hero-reveal:nth-child(5) {
  animation-delay: 540ms;
}

.hero-kicker {
  letter-spacing: 0.08em;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.32);
}

.hero-kicker-line {
  box-shadow: 0 0 18px rgba(250, 34, 52, 0.56);
  transform-origin: left center;
  animation: hero-line-in 780ms cubic-bezier(0.22, 1, 0.36, 1) both 260ms;
}

.hero-title {
  text-wrap: balance;
}

.main-hero-content :deep(p) {
  font-size: 0.98rem;
  font-weight: 600;
  line-height: 1.65;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
}

.hero-stat {
  border-left: 1px solid rgba(255, 255, 255, 0.14);
  padding-left: 0.9rem;
  transition:
    border-color 280ms ease,
    transform 280ms ease;
}

.hero-stat-icon {
  filter: drop-shadow(0 0 12px rgba(250, 34, 52, 0.36));
  transition:
    filter 280ms ease,
    transform 280ms ease;
}

.main-hero:hover .hero-stat {
  border-color: rgba(250, 34, 52, 0.42);
}

.hero-stat:hover {
  transform: translateY(-3px);
}

.hero-stat:hover .hero-stat-icon {
  filter: drop-shadow(0 0 18px rgba(250, 34, 52, 0.56));
  transform: scale(1.06);
}

@keyframes hero-media-drift {
  0% {
    filter: saturate(0.88) contrast(0.96);
    transform: scale(1.045);
  }

  100% {
    filter: saturate(1.06) contrast(1);
    transform: scale(1);
  }
}

@keyframes hero-panel-in {
  0% {
    opacity: 0;
    transform: translateX(-36px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes hero-copy-in {
  0% {
    opacity: 0;
    transform: translateY(22px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes hero-line-in {
  0% {
    opacity: 0;
    transform: scaleX(0);
  }

  100% {
    opacity: 1;
    transform: scaleX(1);
  }
}

@media (min-width: 1024px) {
  .hero-panel {
    clip-path: polygon(0 0, 78% 0, 100% 100%, 0 100%);
  }

  .main-hero:hover .hero-panel {
    width: 100%;
    background-color: rgba(17, 24, 58, 0.92);
    box-shadow: 48px 0 90px rgba(20, 10, 75, 0.42);
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .main-hero-media,
  .hero-panel,
  .hero-reveal,
  .hero-kicker-line {
    animation: none;
  }

  .hero-panel,
  .hero-stat,
  .hero-stat-icon {
    transition-duration: 2ms;
  }
}
</style>
